<template>
  <div class="panel overflow-hidden border border-gray-200 rounded-2xl bg-grey-lightest">
    <div class="flex items-center px-6 pt-5">
      <div class="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-200 shadow-sm">
        <span class="text-sm text-grey-dark whitespace-nowrap">{{ $t('man_hours_group_by') }}</span>
        <button
          type="button"
          class="px-3 py-1.5 rounded-md text-sm font-semibold transition-colors"
          :class="viewMode === 'group'
            ? 'bg-blue text-white'
            : 'bg-white text-grey-darker border border-gray-200 hover:bg-grey-lightest'"
          @click="setViewMode('group')"
        >
          {{ $t('man_hours_by_group') }}
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-md text-sm font-semibold transition-colors"
          :class="viewMode === 'discipline'
            ? 'bg-blue text-white'
            : 'bg-white text-grey-darker border border-gray-200 hover:bg-grey-lightest'"
          @click="setViewMode('discipline')"
        >
          {{ $t('view_by_discipline') }}
        </button>
      </div>
    </div>
    <!-- paneles de horas programadas, horas registradas, desviación de horas y cumplimiento -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 py-5 px-6">
      <div class="flex flex-col gap-2.5 min-h-[8.5rem] p-4 rounded-2xl bg-white border border-gray-200 shadow-md text-blue-dark">
        <div class="flex items-center gap-2">
          <b-icon pack="fas" icon="clock" size="is-small" />
          <span class="text-[0.6875rem] font-bold uppercase tracking-wide leading-tight">{{ $t('scheduled_hours') }}</span>
        </div>
        <div class="mt-auto text-[1.75rem] font-extrabold leading-none tracking-tight">
          <span>{{ formatHours(groupTotals.scheduled) }}</span>
          <span v-if="hasHoursValue(groupTotals.scheduled)" class="ml-0.5 text-lg font-bold">h</span>
        </div>
        <div class="text-xs text-gray-400 leading-snug">{{ $t('man_hours_scheduled_hint') }}</div>
      </div>

      <div class="flex flex-col gap-2.5 min-h-[8.5rem] p-4 rounded-2xl bg-white border border-gray-200 shadow-md text-green-dark">
        <div class="flex items-center gap-2">
          <b-icon pack="fas" icon="users" size="is-small" />
          <span class="text-[0.6875rem] font-bold uppercase tracking-wide leading-tight">{{ $t('registered_hours') }}</span>
        </div>
        <div class="mt-auto text-[1.75rem] font-extrabold leading-none tracking-tight">
          <span>{{ formatHours(groupTotals.registered) }}</span>
          <span v-if="hasHoursValue(groupTotals.registered)" class="ml-0.5 text-lg font-bold">h</span>
        </div>
        <div class="text-xs text-gray-400 leading-snug">{{ $t('man_hours_registered_hint') }}</div>
      </div>

      <div class="flex flex-col gap-2.5 min-h-[8.5rem] p-4 rounded-2xl bg-white border border-gray-200 shadow-md text-orange-dark">
        <div class="flex items-center gap-2">
          <b-icon pack="fas" icon="wave-square" size="is-small" />
          <span class="text-[0.6875rem] font-bold uppercase tracking-wide leading-tight">{{ $t('deviation') }}</span>
        </div>
        <div class="mt-auto text-[1.75rem] font-extrabold leading-none tracking-tight">
          <span>{{ formatSignedHours(groupTotals.deviation) }}</span>
          <span v-if="hasHoursValue(groupTotals.deviation)" class="ml-0.5 text-lg font-bold">h</span>
        </div>
        <div class="text-xs text-gray-400 leading-snug">{{ $t('man_hours_deviation_hint') }}</div>
      </div>

      <div class="flex flex-col gap-2.5 min-h-[8.5rem] p-4 rounded-2xl bg-white border border-gray-200 shadow-md text-grey-darker">
        <div class="flex items-center gap-2">
          <b-icon pack="fas" icon="chart-line" size="is-small" />
          <span class="text-[0.6875rem] font-bold uppercase tracking-wide leading-tight">{{ $t('compliance') }}</span>
        </div>
        <div class="mt-auto text-[1.75rem] font-extrabold leading-none tracking-tight text-black">
          <span>{{ formatPercent(groupTotals.compliance) }}</span>
          <span v-if="hasHoursValue(groupTotals.compliance)" class="ml-0.5 text-lg font-bold">%</span>
        </div>
        <div class="text-xs text-gray-400 leading-snug">{{ $t('man_hours_compliance_hint') }}</div>
      </div>
    </div>

    <!-- grafica HH programadas vs registradas por grupo -->
    <div class="px-6 pb-6">
      <div class="p-4 rounded-2xl bg-white border border-gray-200 shadow-md">
        <div class="flex flex-wrap items-start justify-between gap-3 mb-4">
          <h4 class="text-sm font-bold text-grey-darker m-0">{{ chartTitle }}</h4>
          <nav
            v-if="!isDisciplineMode && chartPath.length"
            class="flex flex-wrap items-center gap-1 text-xs"
            aria-label="breadcrumb"
          >
            <button
              v-for="(crumb, index) in chartBreadcrumb"
              :key="'chart_crumb_' + (crumb.key || 'root')"
              type="button"
              class="inline-flex items-center gap-1 px-2 py-1 rounded-md transition-colors"
              :class="index === chartBreadcrumb.length - 1
                ? 'bg-blue-lightest text-blue-dark font-semibold cursor-default'
                : 'text-blue hover:bg-blue-lightest'"
              :disabled="index === chartBreadcrumb.length - 1"
              @click="navigateChartTo(index)"
            >
              <span>{{ crumb.label }}</span>
              <b-icon
                v-if="index < chartBreadcrumb.length - 1"
                pack="fas"
                icon="chevron-right"
                size="is-small"
              />
            </button>
          </nav>
        </div>

        <div
          v-if="!chartDisplayRows.length"
          class="py-10 text-center text-sm text-grey-dark"
        >
          {{ $t('man_hours_chart_no_subgroups') }}
        </div>
        <column-chart
          v-else
          :data="groupChartRows"
          :colors="['#2563eb', '#16a34a']"
          suffix=" h"
          :library="groupChartLibrary"
          height="320px"
        />
      </div>
    </div>

    <!-- tabla de registro por grupo -->
    <div class="px-6 pb-6">
      <div class="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-md relative">
        <div v-if="isTableLoading" class="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
          <scale-loader />
        </div>
        <div class="overflow-x-auto">
          <table class="man-hour-table w-full min-w-[720px] text-sm">
            <thead class="bg-blue-light">
              <tr class="text-[0.6875rem] uppercase tracking-wide">
                <th class="text-left px-4 py-3 font-bold text-white bg-blue-light">{{ $t('group') }}</th>
                <th class="text-right px-4 py-3 font-bold text-white bg-blue-light">{{ $t('scheduled_hours') }}</th>
                <th class="text-right px-4 py-3 font-bold text-white bg-blue-light">{{ $t('registered_hours') }}</th>
                <th class="text-right px-4 py-3 font-bold text-white bg-blue-light">{{ $t('deviation') }}</th>
                <th class="text-right px-4 py-3 font-bold text-white bg-blue-light">{{ $t('man_hours_compliance_abbr') }}</th>
                <th class="text-left px-4 py-3 font-bold text-white bg-blue-light">{{ $t('man_hours_comparison') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!isTableLoading && !tableDisplayRows.length">
                <td colspan="6" class="px-4 py-8 text-center text-grey-dark">
                  {{ $t('no_results') }}
                </td>
              </tr>
              <template v-for="row in tableDisplayRows">
                <tr
                  :key="row.key"
                  class="border-b border-gray-200"
                  :class="[
                    row.depth === 0 ? 'man-hour-table__row--parent' : 'man-hour-table__row--child',
                    isChartFocusedRow(row) ? 'man-hour-table__row--chart-focus' : '',
                  ]"
                  :style="rowAccentStyle(row)"
                >
                  <td class="px-4 py-3 text-grey-darkest" :class="row.depth > 0 ? 'py-2' : ''">
                    <div class="flex items-center gap-2">
                      <div
                        class="flex items-center gap-2 flex-1 min-w-0"
                        :style="rowIndentStyle(row)"
                        :class="row.hasChildren && !isDisciplineMode ? 'cursor-pointer select-none' : ''"
                        @click="row.hasChildren && !isDisciplineMode ? toggleGroupExpand(row) : null"
                      >
                        <b-icon
                          v-if="!isDisciplineMode && isRowLoadingChildren(row)"
                          pack="fas"
                          icon="sync-alt"
                          size="is-small"
                          custom-class="fa-spin text-grey flex-shrink-0"
                        />
                        <b-icon
                          v-else-if="!isDisciplineMode && row.hasChildren"
                          pack="fas"
                          :icon="isGroupExpanded(row.key) ? 'chevron-down' : 'chevron-right'"
                          size="is-small"
                          class="text-grey flex-shrink-0"
                        />
                        <span
                          v-else-if="row.depth > 0"
                          class="man-hour-table__child-marker flex-shrink-0"
                          aria-hidden="true"
                        />
                        <span v-else class="w-3 flex-shrink-0" />
                        <span
                          v-if="!isDisciplineMode && row.depth > 0"
                          class="inline-flex items-center px-1.5 py-0.5 rounded text-[0.625rem] font-bold uppercase tracking-wide bg-white text-grey-dark border border-grey-light mr-1"
                        >
                          {{ $t('subgroup') }}
                        </span>
                        <span :class="rowLabelClass(row)">
                          {{ row.label }}
                        </span>
                      </div>
                      <button
                        v-if="!isDisciplineMode && row.hasChildren"
                        type="button"
                        class="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-blue-lighter text-blue hover:bg-blue-lightest transition-colors"
                        :title="$t('man_hours_view_in_chart')"
                        :disabled="isRowLoadingChildren(row)"
                        @click.stop="focusChartOnGroup(row)"
                      >
                        <b-icon pack="fas" icon="chart-column" size="is-small" />
                      </button>
                    </div>
                  </td>
                  <td
                    class="px-4 py-3 text-right whitespace-nowrap"
                    :class="rowValueClass(row, 'scheduled')"
                  >
                    {{ formatHours(row.scheduled) }}
                  </td>
                  <td
                    class="px-4 py-3 text-right whitespace-nowrap"
                    :class="rowValueClass(row, 'registered')"
                  >
                    {{ formatHours(row.registered) }}
                  </td>
                  <td class="px-4 py-3 text-right whitespace-nowrap" :class="row.depth > 0 ? 'py-2' : ''">
                    <span
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-semibold"
                      :class="[
                        deviationBadgeClass(row.deviation),
                        row.depth > 0 ? 'text-[0.6875rem]' : 'text-xs',
                      ]"
                    >
                      <b-icon
                        v-if="isSignificantDeviation(row.deviation)"
                        pack="fas"
                        icon="chart-line"
                        size="is-small"
                      />
                      <span>{{ formatTableDeviation(row.deviation) }}</span>
                    </span>
                  </td>
                  <td
                    class="px-4 py-3 text-right whitespace-nowrap"
                    :class="row.depth > 0 ? 'py-2 text-sm font-semibold text-grey-darker' : 'font-bold text-black'"
                  >
                    {{ formatCompliance(row.compliance) }}
                  </td>
                  <td class="px-4 py-3" :class="row.depth > 0 ? 'py-2' : ''">
                    <div
                      class="flex flex-col gap-1 w-full max-w-[140px]"
                      :class="row.depth > 0 ? 'opacity-80 max-w-[120px]' : ''"
                    >
                      <div class="h-1.5 rounded-full bg-grey-lighter overflow-hidden">
                        <div
                          class="h-full rounded-full"
                          :class="row.depth > 0 ? 'bg-blue-light' : 'bg-blue-dark'"
                          :style="{ width: comparisonBarWidth(row.scheduled, row.scheduled) + '%' }"
                        />
                      </div>
                      <div class="h-1.5 rounded-full bg-grey-lighter overflow-hidden">
                        <div
                          class="h-full rounded-full"
                          :class="row.depth > 0 ? 'bg-green-light' : 'bg-green-dark'"
                          :style="{ width: comparisonBarWidth(row.registered, row.scheduled) + '%' }"
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr class="bg-blue-lightest">
                <td class="px-4 py-3 font-bold uppercase text-black whitespace-nowrap">
                  {{ $t('total') }} {{ $t('project') }}
                </td>
                <td class="px-4 py-3 text-right font-bold text-blue-dark whitespace-nowrap">
                  {{ formatHours(groupTotals.scheduled) }}
                </td>
                <td class="px-4 py-3 text-right font-bold text-green-dark whitespace-nowrap">
                  {{ formatHours(groupTotals.registered) }}
                </td>
                <td class="px-4 py-3 text-right whitespace-nowrap">
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold"
                    :class="deviationBadgeClass(groupTotals.deviation)"
                  >
                    <b-icon
                      v-if="isSignificantDeviation(groupTotals.deviation)"
                      pack="fas"
                      icon="chart-line"
                      size="is-small"
                    />
                    <span>{{ formatTableDeviation(groupTotals.deviation) }}</span>
                  </span>
                </td>
                <td class="px-4 py-3 text-right font-bold text-black whitespace-nowrap">
                  {{ formatCompliance(groupTotals.compliance) }}
                </td>
                <td class="px-4 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import VueChartkick from 'vue-chartkick'
import Chart from 'chart.js'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'

Vue.use(VueChartkick, { adapter: Chart })

const SIGNIFICANT_DEVIATION_THRESHOLD = 10000

function appendVisibleManHourRows(row, rows, isExpanded) {
  rows.push(row)

  if (!row.hasChildren || !isExpanded(row.key)) {
    return
  }

  row.children.forEach((child) => {
    appendVisibleManHourRows(child, rows, isExpanded)
  })
}

function findManHourNodeByKey(rows, key) {
  for (const row of rows) {
    if (row.key === key) {
      return row
    }

    const found = findManHourNodeByKey(row.children, key)
    if (found) {
      return found
    }
  }

  return null
}

function buildManHourPathToKey(rows, targetKey, acc = []) {
  for (const row of rows) {
    const nextPath = [...acc, row.key]

    if (row.key === targetKey) {
      return nextPath
    }

    if (row.children.length) {
      const found = buildManHourPathToKey(row.children, targetKey, nextPath)
      if (found.length) {
        return found
      }
    }
  }

  return []
}

export default {
  name: 'DashboardSubpanelManHour',

  components: {
    ScaleLoader,
  },
  props: {
    projectId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      viewMode: 'group',
      rootGroups: [],
      childrenByParentId: {},
      loadedParentIds: {},
      loadingParentIds: {},
      loadingExpandKey: null,
      isTableLoading: false,
      expandedGroups: {},
      chartPath: [],
    }
  },

  watch: {
    projectId: {
      immediate: true,
      handler(projectId) {
        if (projectId) {
          this.resetAndLoadRootGroups()
        } else {
          this.resetGroupData()
        }
      },
    },
  },

  computed: {
    isDisciplineMode() {
      return this.viewMode === 'discipline'
    },

    groupRows() {
      return (this.rootGroups || []).map((group) => this.buildRowFromApiGroup(group, 0))
    },

    chartFocusNode() {
      if (!this.chartPath.length) {
        return null
      }

      return findManHourNodeByKey(this.groupRows, this.chartPath[this.chartPath.length - 1])
    },

    chartBreadcrumb() {
      const crumbs = [{ key: null, label: this.$t('root_groups') }]

      this.chartPath.forEach((key) => {
        const node = findManHourNodeByKey(this.groupRows, key)
        if (node) {
          crumbs.push({ key: node.key, label: node.label })
        }
      })

      return crumbs
    },

    chartTitle() {
      if (this.isDisciplineMode) {
        return this.$t('man_hours_scheduled_vs_registered_by_discipline')
      }

      if (!this.chartFocusNode) {
        return this.$t('man_hours_scheduled_vs_registered_by_group')
      }

      return this.$t('man_hours_chart_level_title', { group: this.chartFocusNode.label })
    },

    chartDisplayRows() {
      if (this.isDisciplineMode || !this.chartPath.length) {
        return this.groupRows
      }

      return this.chartFocusNode ? this.chartFocusNode.children : []
    },

    tableDisplayRows() {
      const rows = []

      this.groupRows.forEach((group) => {
        appendVisibleManHourRows(group, rows, (key) => this.isGroupExpanded(key))
      })

      return rows
    },

    groupTotals() {
      const scheduled = this.groupRows.reduce((sum, row) => sum + row.scheduled, 0)
      const registered = this.groupRows.reduce((sum, row) => sum + row.registered, 0)
      const deviation = registered - scheduled
      const compliance = scheduled > 0 ? Math.round((registered / scheduled) * 100) : 0

      return { scheduled, registered, deviation, compliance }
    },

    groupChartRows() {
      const scheduled = []
      const registered = []

      this.chartDisplayRows.forEach((row) => {
        scheduled.push([row.label, row.scheduled])
        registered.push([row.label, row.registered])
      })

      return [
        { name: this.$t('scheduled_hours'), data: scheduled },
        { name: this.$t('registered_hours'), data: registered },
      ]
    },

    groupChartLibrary() {
      return {
        legend: { display: true, position: 'bottom' },
        tooltips: {
          callbacks: {
            label(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label || ''
              const value = tooltipItem.yLabel
              if (value >= 1e6) return `${label}: ${(value / 1e6).toFixed(2)}M h`
              if (value >= 1e3) return `${label}: ${(value / 1e3).toFixed(1)}K h`
              return `${label}: ${Math.round(value)} h`
            },
          },
        },
        scales: {
          xAxes: [{ ticks: { maxRotation: 45, minRotation: 45, fontSize: 11 } }],
          yAxes: [{
            ticks: {
              callback(v) {
                if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`
                if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`
                return v
              },
            },
            gridLines: { borderDash: [4, 4] },
          }],
        },
      }
    },
  },

  methods: {
    async setViewMode(mode) {
      if (this.viewMode === mode) {
        return
      }

      this.viewMode = mode
      await this.resetAndLoadRootGroups()
    },

    resetGroupData() {
      this.rootGroups = []
      this.childrenByParentId = {}
      this.loadedParentIds = {}
      this.loadingParentIds = {}
      this.loadingExpandKey = null
      this.expandedGroups = {}
      this.chartPath = []
    },

    async resetAndLoadRootGroups() {
      this.resetGroupData()
      await this.loadGroups(null)
    },

    async loadGroups(parentId = null) {
      if (!this.projectId) {
        return
      }

      if (this.isDisciplineMode && parentId != null) {
        return
      }

      const cacheKey = this.isDisciplineMode
        ? 'disciplines'
        : (parentId == null ? 'root' : String(parentId))
      if (this.loadedParentIds[cacheKey]) {
        return
      }

      const isRoot = parentId == null
      if (isRoot) {
        this.isTableLoading = true
      } else {
        this.loadingParentIds = {
          ...this.loadingParentIds,
          [cacheKey]: true,
        }
      }

      const params = { with_groups: 1, p: this.projectId}
      if (this.isDisciplineMode) {
        params.only_disciplines = 1
      } else if (parentId != null) {
        params.parent_id = parentId
      }

      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/dashboard/man-hours/groups`,
          { params }
        )
        const groups = (data && data.groups) ? data.groups : []

        if (isRoot) {
          this.rootGroups = groups
        } else {
          this.childrenByParentId = {
            ...this.childrenByParentId,
            [parentId]: groups,
          }
        }

        this.loadedParentIds = {
          ...this.loadedParentIds,
          [cacheKey]: true,
        }
      } catch (error) {
        if (isRoot) {
          this.rootGroups = []
        }
        this.$notify.error('error_loading_activities')
      } finally {
        if (isRoot) {
          this.isTableLoading = false
        } else {
          const nextLoading = { ...this.loadingParentIds }
          delete nextLoading[cacheKey]
          this.loadingParentIds = nextLoading
          this.loadingExpandKey = null
        }
      }
    },

    buildRowFromApiGroup(group, depth) {
      const scheduled = Number(group.scheduled_man_hours) || 0
      const registered = Number(group.registered_man_hours) || 0
      const deviation = Number(group.deviation_hours) || 0
      const compliance = Number(group.compliance_percent) || 0

      const base = {
        key: String(group.id),
        id: group.id,
        label: group.name || group.code || String(group.id),
        depth,
        scheduled,
        registered,
        deviation,
        compliance,
      }

      if (this.isDisciplineMode) {
        return { ...base, children: [], hasChildren: false }
      }

      const children = (this.childrenByParentId[group.id] || [])
        .map((child) => this.buildRowFromApiGroup(child, depth + 1))

      return {
        ...base,
        children,
        hasChildren: Number(group.children_count || 0) > 0,
      }
    },

    isRowLoadingChildren(row) {
      return this.loadingExpandKey === row.key || Boolean(this.loadingParentIds[String(row.id)])
    },

    async ensureChildrenLoaded(row) {
      if (this.isDisciplineMode || !row || !row.hasChildren || this.loadedParentIds[String(row.id)]) {
        return
      }

      this.loadingExpandKey = row.key
      await this.loadGroups(row.id)
    },

    async toggleGroupExpand(row) {
      if (this.isDisciplineMode || !row || !row.hasChildren) {
        return
      }

      const willExpand = !this.isGroupExpanded(row.key)

      if (willExpand) {
        await this.ensureChildrenLoaded(row)
        this.expandedGroups = {
          ...this.expandedGroups,
          [row.key]: true,
        }
        return
      }

      this.expandedGroups = {
        ...this.expandedGroups,
        [row.key]: false,
      }
    },

    isGroupExpanded(key) {
      return Boolean(this.expandedGroups[key])
    },

    async focusChartOnGroup(row) {
      if (this.isDisciplineMode || !row || !row.hasChildren) {
        return
      }

      await this.ensureChildrenLoaded(row)

      this.expandedGroups = {
        ...this.expandedGroups,
        [row.key]: true,
      }

      this.chartPath = buildManHourPathToKey(this.groupRows, row.key)
    },

    async navigateChartTo(index) {
      if (index <= 0) {
        this.chartPath = []
        return
      }

      const nextPath = this.chartPath.slice(0, index)
      const focusKey = nextPath[nextPath.length - 1]
      const focusRow = findManHourNodeByKey(this.groupRows, focusKey)

      if (focusRow) {
        await this.ensureChildrenLoaded(focusRow)
      }

      this.chartPath = nextPath
    },

    isChartFocusedRow(row) {
      return Boolean(this.chartFocusNode && this.chartFocusNode.key === row.key)
    },

    rowIndentStyle(row) {
      if (row.depth === 0) {
        return null
      }

      return {
        paddingLeft: `${0.5 + row.depth * 1.25}rem`,
      }
    },

    rowAccentStyle(row) {
      if (row.depth === 0) {
        return null
      }

      const accentColors = ['#ADC2EA', '#7D99D4', '#5577BB', '#385DA5']

      return {
        backgroundColor: row.depth % 2 === 1 ? '#f1f5f9' : '#e8eef5',
        boxShadow: `inset ${Math.min(row.depth, 4) * 3}px 0 0 ${accentColors[Math.min(row.depth - 1, accentColors.length - 1)]}`,
      }
    },

    rowLabelClass(row) {
      if (row.depth === 0) {
        return 'font-semibold text-grey-darkest'
      }

      if (row.depth === 1) {
        return 'text-sm text-grey-darker font-normal'
      }

      return 'text-xs text-grey-dark font-normal'
    },

    rowValueClass(row, type) {
      if (row.depth === 0) {
        return type === 'scheduled'
          ? 'font-semibold text-blue-dark'
          : 'font-semibold text-green-dark'
      }

      if (row.depth === 1) {
        return type === 'scheduled'
          ? 'py-2 text-sm font-medium text-blue-light'
          : 'py-2 text-sm font-medium text-green'
      }

      return type === 'scheduled'
        ? 'py-2 text-xs font-medium text-blue-lighter'
        : 'py-2 text-xs font-medium text-green-light'
    },

    hasHoursValue(value) {
      return value !== null && value !== undefined && Number.isFinite(Number(value))
    },

    formatHours(value) {
      if (!this.hasHoursValue(value)) {
        return '—'
      }

      return Math.abs(Number(value)).toLocaleString('es-ES', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })
    },

    formatSignedHours(value) {
      if (!this.hasHoursValue(value)) {
        return '—'
      }

      const num = Number(value)
      const formatted = Math.abs(num).toLocaleString('es-ES', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })

      if (num < 0) {
        return `- ${formatted}`
      }

      if (num > 0) {
        return `+ ${formatted}`
      }

      return formatted
    },

    formatPercent(value) {
      if (!this.hasHoursValue(value)) {
        return '—'
      }

      return Math.round(Number(value)).toLocaleString('es-ES')
    },

    formatCompliance(value) {
      if (!this.hasHoursValue(value)) {
        return '—'
      }

      return `${Math.round(Number(value))}%`
    },

    formatTableDeviation(value) {
      if (!this.hasHoursValue(value)) {
        return '—'
      }

      const num = Number(value)
      const formatted = Math.abs(num).toLocaleString('es-ES', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })

      if (num < 0) {
        return `- ${formatted} h`
      }

      if (num > 0) {
        return `+ ${formatted} h`
      }

      return `${formatted} h`
    },

    isSignificantDeviation(value) {
      return this.hasHoursValue(value) && Math.abs(Number(value)) >= SIGNIFICANT_DEVIATION_THRESHOLD
    },

    deviationBadgeClass(value) {
      if (this.isSignificantDeviation(value)) {
        return 'bg-orange-lightest text-orange-dark'
      }

      return 'bg-grey-lighter text-grey-darker'
    },

    comparisonBarWidth(value, reference) {
      const ref = Number(reference) || 0
      if (ref <= 0) {
        return 0
      }

      return Math.min(100, Math.round((Number(value) / ref) * 100))
    },
  },
}
</script>

<style scoped>
.man-hour-table thead th {
  background-color: #7D99D4;
  color: #fff !important;
  border: none;
}

.man-hour-table__row--parent {
  background-color: #fff;
}

.man-hour-table__row--child {
  border-top: 1px dashed #DAE1E7;
}

.man-hour-table__child-marker {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background-color: #ADC2EA;
}

.man-hour-table__row--chart-focus {
  box-shadow: inset 0 0 0 2px #5577BB;
}
</style>
